"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Image as DatoImage } from "react-datocms/image";
import { useIntersectionObserver } from "usehooks-ts";

import { type SearchPassengers } from "@trip/constants";

import { AngleLeftSmallIcon } from "~/components/icons/angle-left-small";
import { AngleRightSmallIcon } from "~/components/icons/angle-right-small";
import { H3 } from "~/components/typography";
import { Button } from "~/components/ui/button";
import { type ProductId } from "~/lib/tours";

import type { Day } from "../_lib/trip";
import { TourCard } from "./tour-card";

function Cards<
  T extends {
    id: string | number;
    datoTour: Day["tours"][number]["tour"];
  },
>({
  items,
  CardComponent,
}: {
  CardComponent: (props: T) => ReactNode;
  items: T[];
}) {
  const t = useTranslations("TripDetails");
  const tCommon = useTranslations("Common");
  const ref = useRef<HTMLDivElement>(null);
  const [itemWidth, setItemWidth] = useState(0);
  const intersection = useIntersectionObserver({
    root: ref.current,
    freezeOnceVisible: true,
  });
  useEffect(() => {
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    setItemWidth(
      (ref.current?.firstChild as HTMLDivElement | undefined)?.clientWidth ?? 0,
    );
  }, [intersection.isIntersecting]);

  return (
    <>
      <div className="col-span-2 col-start-2 mb-4 flex items-center gap-2 sm:col-span-1 sm:col-start-3">
        <H3 className="grow">{t("addExperience")}</H3>
        <div className="flex gap-2">
          <Button
            size="circle"
            onClick={(event) => {
              event.preventDefault();
              if (ref.current) {
                ref.current.scrollBy({
                  left: -1 * itemWidth,
                  behavior: "smooth",
                });
              }
            }}
          >
            <AngleLeftSmallIcon />
            <span className="sr-only">{tCommon("next")}</span>
          </Button>
          <Button
            size="circle"
            onClick={(event) => {
              event.preventDefault();
              if (ref.current) {
                ref.current.scrollBy({
                  left: itemWidth,
                  behavior: "smooth",
                });
              }
            }}
          >
            <AngleRightSmallIcon />
            <span className="sr-only">{tCommon("next")}</span>
          </Button>
        </div>
      </div>
      <div className="col-span-3 col-start-2 -ml-6 mb-16 sm:col-span-2 sm:col-start-3">
        <div
          className="flex snap-x snap-mandatory justify-between overflow-x-auto overflow-y-hidden py-px"
          ref={ref}
        >
          {items.map((item) => (
            <div
              className="box-content w-[calc(100vw-48px)] shrink-0 snap-start pb-6 pl-6 last:pr-24 md:w-[695px]"
              key={item.id}
            >
              <div className="grid min-h-[560px] gap-4 rounded-2xl border border-neutral-200 p-5 md:min-h-0 md:grid-cols-2">
                <CardComponent {...item} />
                <div className="aspect-2/3 relative hidden overflow-hidden rounded-lg md:block">
                  <DatoImage
                    data={item.datoTour.heroImage.responsiveImage}
                    layout="fill"
                    objectFit="cover"
                  />
                  <div className="absolute inset-0 rounded-lg shadow-[inset_0_0_0_1px_rgba(0,0,0,.1)]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export function TourCards({
  date,
  datoTours,
  searchPassengers,
  dayNumber,
  datoDayId,
}: {
  date: Date;
  datoDayId: string;
  dayNumber: number;
  searchPassengers: SearchPassengers;
  datoTours: Day["tours"];
}) {
  return (
    <Cards
      items={datoTours.flatMap((tour) => {
        const datoTour = tour.tour;
        let productId: ProductId;
        if (datoTour.bokunExperienceId) {
          productId = { type: "bokun", id: datoTour.bokunExperienceId };
        } else if (datoTour.linktivityActivityId) {
          productId = {
            type: "linktivity",
            id: datoTour.linktivityActivityId,
          };
        } else {
          return [];
        }
        return [
          {
            id: datoTour.id,
            datoTour: tour.tour,
            date,
            searchPassengers,
            datoDayId,
            dayNumber,
            productId,
          },
        ];
      })}
      CardComponent={TourCard}
    />
  );
}
